import SettingsIcon from '@mui/icons-material/Settings';
import { IconButton, Menu, MenuItem, Toolbar, Typography } from '@mui/material';
import { ReactNode, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export interface WidgetSettingsHeaderProps {
  title: ReactNode;
  onDelete: () => void;
  readOnly?: boolean;
  allowDelete?: boolean;
  /** Rendered before the delete action in the settings menu. */
  menuItems?: ReactNode;
  /** Extra controls shown in the header bar (e.g. console filter/clear). */
  toolbarEnd?: ReactNode;
}

const WidgetSettingsHeader = ({ title, onDelete, readOnly, allowDelete = true, menuItems, toolbarEnd }: WidgetSettingsHeaderProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleSettingsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (readOnly) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleDelete = () => {
    handleClose();
    onDelete();
  };

  return (
    <Toolbar
      className="widget-header"
      variant="dense"
      sx={{
        color: 'text.primary',
        justifyContent: 'center',
        flexShrink: 0,
        pr: 1,
        pl: 2,
        pt: 0,
        pb: 0,
        minHeight: '50px',
      }}
    >
      <Typography
        component="div"
        sx={{
          flex: 1,
          textAlign: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '0.75rem',
        }}
      >
        {title}
      </Typography>
      {toolbarEnd}
      <IconButton
        color="inherit"
        aria-label={t('aria-label.settings-button', 'settings')}
        aria-controls={open ? 'widget-settings-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleSettingsClick}
        sx={{ flexShrink: 0, ml: toolbarEnd ? 0 : 'auto' }}
      >
        <SettingsIcon />
      </IconButton>
      <Menu
        id="widget-settings-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableRestoreFocus
        slotProps={{
          list: { 'aria-labelledby': 'settings-button', autoFocusItem: false },
        }}
      >
        {menuItems}
        {allowDelete ? (
          <MenuItem onClick={handleDelete}>
            <Trans i18nKey="widgetBase.menuItem.delete-widget">Delete Widget</Trans>
          </MenuItem>
        ) : null}
      </Menu>
    </Toolbar>
  );
};

export default WidgetSettingsHeader;
